"use client";

import {JSX, useEffect, useRef, useState} from "react";

import styles from "@@/styles/components/shared/LandingSection.module.scss";

import { ralewayBold, ralewayRegular, exo2Bold, exo2SemiBold } from "@@/utils/fonts";

import Scrolling from "../ui/Scrolling";
import Image, {StaticImageData} from "next/image";
import ProgressiveImage from "@@/utils/ProgressiveImage";
import {cacheTag} from "next/dist/server/use-cache/cache-tag";
import Button from "@@/components/ui/Button";
import logoMediawind from "@@/public/img/logo_icon_img.png";
import useLandingEffects from "@@/hooks/useLandingEffects";
import useAppearOnScroll from "@@/hooks/useAppearsOnScroll";
import LogoStroke from "@@/public/img/shared/logo_stroke.svg";
import VideoPoster from "@@/public/img/homepage/video_poster.webp";
import Video from "@@/public/media/video_home_page_proposition.v3.mp4";
import HeroVideoAuto from "@@/components/shared/HeroVideoAuto";
interface ILandingSectionProps {
	title: string;
	text?: string;
	img4k?: StaticImageData;
	img?: StaticImageData;
	home?: boolean;
	logo?: boolean;
	cta?: string;
	ctaUrl?: string;
	sectionId?: string;
	className?: string;
	imageClassName?: string;
	logoStroke?: boolean;
	video?: boolean;
}

export default function LandingSection(props: ILandingSectionProps): JSX.Element {
	const { title, text, img, home, img4k, logo, cta, ctaUrl, sectionId, className, imageClassName, logoStroke, video} = props;

	const [scrollingOpacity, setScrollingOpacity] = useState<number>(1);

	const containerRef = useRef<HTMLDivElement>(null);
	const leftLogoRef = useRef<HTMLImageElement>(null);
	const rightLogoRef = useRef<HTMLImageElement>(null);

	const animatedBtn = useAppearOnScroll<HTMLDivElement>({
		from: "fromBottom",
		duration: 1000,
		delay: 500,
		offset: -100
	})

	useLandingEffects({
		containerRef,
		leftLogoRef,
		rightLogoRef,
		active: logo === true,
	});

	const ref = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const thresholdArray = [];
		let i = 0;

		while (i <= 1) {
			thresholdArray.push(i);
			i += .2;
		}

		const observer = new IntersectionObserver((entries) => {
			entries.forEach((entry) => {
				setScrollingOpacity(entry.intersectionRatio < 1 ? 0 : 1);
			});
		}, {
			threshold: thresholdArray,
		});

		if (ref.current) {
			observer.observe(ref.current);
		}
	}, [ref]);

	return (
		<section
			id={sectionId}
			ref={ref}
			className={`${styles.landing} ${home ? styles.home : ""} ${logo ? styles.logoStyle : ''} ${className} align-start-mobile`}
			style={!logo ? { background: `linear-gradient(0deg, rgba(0, 63, 188, 0.71) 0%, rgba(0, 63, 188, 0.71) 100%)`} : {}}
		>
			{
				img && img4k && !video ?
				<ProgressiveImage
					lowSrc={img}
					highSrc={img4k}
					alt="Landing Mediawind"
					width={img4k.width ?? 1920}
					height={img4k.height ?? 1080}
					quality={60}
					className={`${imageClassName} ${styles.backgroundImage}`}
					classLowRes={styles.lowRes}
				/> : ''
			}
			{video && (
				<HeroVideoAuto
					src={Video}
					poster={VideoPoster.src}
					aspectRatio="16 / 9"
					delayMs={1000}
					loop
					requireVisible
					className={styles.backgroundVideo}
				/>
			)}
			<h2 className={`${exo2Bold.className} fs-4 justify-start-mobile`} dangerouslySetInnerHTML={{ __html: title }}>
			</h2>
			{text && text !== '' ? <p className={`${styles.description} ${exo2SemiBold.className} fs-1_5 justify-start-mobile`}>{text}</p> : ''}
			{cta && ctaUrl && cta !== '' ? <div ref={animatedBtn.ref} className={`${animatedBtn.className} ${exo2SemiBold.className} min-w-100-mobile`} style={animatedBtn.style}><Button  href={ctaUrl} title={cta} bgColor={"var(--blue-600)"} textColor={"white"}/></div> : ''}

			{/*home && (
				<Scrolling opacity={scrollingOpacity} />
			)*/}
			{logo ?
				<span className={styles.logoContainer} ref={containerRef}>
					<Image
						src={logoMediawind}
						alt="Logo dark"
						className={`${styles.logo} ${styles.left}`}
						ref={leftLogoRef}
						priority
					/>
					<Image
						src={logoMediawind}
						alt="Logo dark"
						className={`${styles.logo} ${styles.right}`}
						ref={rightLogoRef}
						priority
					/>
				</span>
			:''}
			{logoStroke && (
				<Image src={LogoStroke} alt={`logo mediawind`} className={`position-absolute right--4 bottom--4 ${styles.logoStroke}`}/>
			)}
		</section>
	);
}
